import { clsx } from 'clsx'
import { Dot } from './icons'

export type PillTone = 'neutral' | 'ok' | 'warn' | 'danger' | 'ink'

/**
 * `dot={false}` makes it a plain tag rather than a status. The dot means "this changes": Invited, Submitted, Active.
 * Fixed labels (a role, Test, Built in, "This client only") go without it; `size="xs"` is the small tag
 * that sits inline beside a line of text. Square like everything else in the brand.
 */
export function StatusPill({ tone = 'neutral', dot = true, size = 'sm', children, className }: { tone?: PillTone; dot?: boolean; size?: 'sm' | 'xs'; children: React.ReactNode; className?: string }) {
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 border font-medium uppercase tracking-eyebrow whitespace-nowrap',
        size === 'sm' ? 'px-2 py-0.5 text-[0.6875rem]' : 'px-1.5 py-px text-[0.625rem] leading-4',
        tone === 'neutral' && 'border-gray-300 text-dim',
        tone === 'ok' && 'border-ok/40 bg-ok-wash text-ok',
        tone === 'warn' && 'border-warn/40 bg-warn-wash text-warn',
        tone === 'danger' && 'border-danger/40 bg-danger-wash text-danger',
        tone === 'ink' && 'border-ink bg-ink text-paper',
        className,
      )}
    >
      {dot && <Dot size={6} />}
      {children}
    </span>
  )
}

/** Client lifecycle status → pill. Keep in sync with shared ClientStatus. */
export const CLIENT_STATUS_PILL: Record<string, { tone: PillTone; label: string }> = {
  draft: { tone: 'neutral', label: 'Draft' },
  invited: { tone: 'warn', label: 'Invited' },
  in_progress: { tone: 'ink', label: 'In progress' },
  submitted: { tone: 'ok', label: 'Submitted' },
  archived: { tone: 'neutral', label: 'Archived' },
}
